/* eslint-disable react/jsx-one-expression-per-line */
import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import ReactPlayer from 'react-player';
import { poslumaDarkBlue, universalStyles } from '../../../../app/theme';
import LearningCheckQuestions from '../../../micro/learningCheck/LearningCheckQuestions';
import { BASE_URL, IMAGE_PATHS, VIDEO_PATHS } from '../../../../constants/paths.constants';

const useStyles = makeStyles(() => ({
  ...universalStyles,
  diagram: {
    width: '80%',
    marginLeft: '10%',
  },
  image: {
    width: '90%',
    height: 'auto',
    marginLeft: 30,
    marginRight: 30,
    padding: '20px 0 20px 0',
  },
  image2: {
    width: '90%',
    height: 'auto',
    marginLeft: 30,
    marginRight: 30,
    padding: '20px 0 20px 0',
    marginTop: -30,
  },
  chemicalImage: {
    width: '90%',
    height: 'auto',
    marginLeft: 30,
    marginRight: 30,
    marginTop: -25,
    marginBottom: 10,
    padding: '20px 0 20px 0',
  },
  imageText: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: -30,
    paddingBottom: 30,
  },
  imageText2: {
    fontSize: 12,
    textAlign: 'center',
    marginTop: 30,
    paddingBottom: 30,
  },
  video: {
    width: '80%',
    aspectRatio: '16/9',
    margin: '25px 10% 6px',
  },
  externalLink: {
    color: poslumaDarkBlue,
    textTransform: 'none',
    textDecoration: 'underline',
    padding: 0,
    fontSize: 18,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  abbreviations: {
    paddingTop: 40,
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  abbreviationContent: {
    margin: '0 0 12px 0',
    fontSize: 14,
  },
  link: {
    color: poslumaDarkBlue,
    fontSize: 18,
    fontWeight: 600,
    textDecoration: 'underline',
  },
  superscript: {
    fontSize: 11,
  },
  superscript2: {
    fontSize: 8,
  },
  superscriptHeader: {
    fontSize: 13,
  },
  fpo: {
    paddingTop: 10,
    paddingBottom: 40,
    fontSize: 24,
    color: 'magenta',
    textAlign: 'center',
  },
}));

type AcquisitionPage03Props = {
  setNextDisabled: (disabled: boolean) => void;
};

const AcquisitionPage03 = ({ setNextDisabled }: AcquisitionPage03Props): JSX.Element => {
  const classes = useStyles();

  return (
    <>
      <div className={classes.poslumaBox}>
        <div className={classes.whiteBox}>
          <Typography variant="h4">Prostate Cancer</Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                Adenocarcinoma of the prostate (PCa) is common
                <sup className={classes.superscript}>1,2</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>
                <span>
                  Most prevalent cancer in men in the developed world
                  <sup className={classes.superscript}>2</sup>
                </span>
              </li>
              <li>
                <span>
                  Prostate cancer is the
                  <strong> sixth </strong>
                  leading cause of cancer death in men
                </span>
              </li>
            </ul>
            <li>
              <span>
                Computed tomography (CT), magnetic resonance imaging (MRI), and bone scans are used for detecting
                disease in
                <strong>&nbsp;patients with biochemical recurrence (BCR)</strong>
                <sup className={classes.superscript}>3</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>
                <span>
                  These modalities can have poor sensitivity for identifying sites of recurrence
                  <strong>&nbsp;especially at low PSA levels</strong>
                  <sup className={classes.superscript}>3</sup>
                </span>
              </li>
              <li>
                <span>
                  PET (<sup className={classes.superscript}>18</sup>
                  F-fluciclovine,
                  <sup className={classes.superscript}>68</sup>
                  Ga- or
                  <sup className={classes.superscript}>18</sup>
                  F-PSMA-targeted) has demonstrated added value in identifying recurrent disease
                  <sup className={classes.superscript}>3</sup>
                </span>
              </li>
            </ul>
          </ul>
          <Typography variant="h4">Prostate-Specific Membrane Antigen</Typography>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                Cell surface transmembrane glycoprotein
                <sup className={classes.superscript}>1,4</sup>
              </span>
            </li>
            <li>
              <span>
                Expressed in prostate epithelium and elevated in prostate cancer, compared with healthy tissue
                <sup className={classes.superscript}>1</sup>
              </span>
            </li>

            <li>
              Enzymatic & endocytic functions
              <sup className={classes.superscript}>1,4</sup>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Neurotransmitter metabolism</li>
              <li>Folate metabolism (folate hydrolase)</li>
              <li>Internalization on binding of ligands</li>
            </ul>
          </ul>
          <Typography variant="h4">POSLUMA Radiohybrid</Typography>
          <Typography paragraph variant="body1">
            Radiohybrids are monozygotic chemical twins…
            <sup className={classes.superscript}>5</sup>
          </Typography>
          <img
            className={classes.image2}
            src={`${BASE_URL}${IMAGE_PATHS.IMAGING_THERAPY.path}`}
            alt={`${IMAGE_PATHS.IMAGING_THERAPY.alt}`}
          />
          <Typography variant="h4">Molecular Structure of POSLUMA</Typography>
          <img
            className={classes.chemicalImage}
            src={`${BASE_URL}${IMAGE_PATHS.POSLUMA_CHEMICAL_STRUCTURE.path}`}
            alt={`${IMAGE_PATHS.POSLUMA_CHEMICAL_STRUCTURE.alt}`}
          />
          <Typography paragraph variant="body1" className={classes.imageText}>
            <strong>POSLUMA (flotufolastat F 18) </strong>
            <strong>is a fluorine-18 (</strong>
            <sup className={classes.superscript}>18</sup>
            <strong>
              F) radiolabeled, radiohybrid PSMA imaging agent, which includes a DOTAGA chelator ring structure that
              forms a complex with a nonradioactive, naturally occurring gallium ion. Radioactive
            </strong>
            <sup className={classes.superscript}>18</sup>
            <strong>
              F is covalently bound to a silicon-fluorine acceptor moiety. The molecular weight of POSLUMA is 1537.3
              g/mol.
            </strong>
            <sup className={classes.superscript}>6</sup>
          </Typography>
          <Typography variant="h4">Mechanism of Action of POSLUMA</Typography>
          <div className={classes.video}>
            <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.MECHANICS_OF_ACTION.path} controls />
          </div>
          <div>
            <Typography variant="h4">Biodistribution: The Pharmacokinetics of POSLUMA</Typography>
          </div>
          <img
            className={classes.image}
            src={`${BASE_URL}${IMAGE_PATHS.HEALTHY_VOLUNTEER_PET.path}`}
            alt={`${IMAGE_PATHS.HEALTHY_VOLUNTEER_PET.alt}`}
          />
          <ul className={classes.poslumaList}>
            <li>
              <span>
                <strong>Distribution after intravenous administration (% of administered activity)</strong>
                <sup className={classes.superscript}>6</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Liver: 15.8%</li>
              <li>Heart blood pool: 7.4%</li>
              <li>Kidneys: 3.2%</li>
              <li>POSLUMA is cleared from the blood rapidly</li>
            </ul>
          </ul>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                <strong>Visually, there is increased physiologic uptake in</strong>
                <sup className={classes.superscript}>6</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Salivary and lacrimal glands</li>
              <li>Liver</li>
              <li>Spleen</li>
              <li>Kidneys (parenchyma)</li>
              <li>Bowels</li>
            </ul>
          </ul>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                <strong>Moderate, nonspecific uptake can occur in</strong>
                <sup className={classes.superscript}>6</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>Thyroid</li>
              <li>Axillary, hilar, mediastinal, and inguinal lymph nodes</li>
            </ul>
          </ul>
          <ul className={classes.poslumaList}>
            <li>
              <span>
                <strong>Elimination</strong>
                <sup className={classes.superscript}>6</sup>
              </span>
            </li>
            <ul className={classes.poslumaNestedList}>
              <li>
                <span>Flotufolastat F 18 does not undergo metabolism up to 50 minutes post injection</span>
              </li>
              <li>
                <span>Urinary excretion (% of administered activity)</span>
              </li>
              <li>
                <span>Approximately 7% in the first 2 hours post injection</span>
              </li>
              <li>
                <span>Approximately 15% after 4.5 hours post injection</span>
              </li>
            </ul>
          </ul>
          <div>
            <div className={classes.video}>
              <ReactPlayer className={classes.responsiveVideo} url={VIDEO_PATHS.POSLUMA_UPTAKE_AVI.path} controls />
            </div>
            <Typography className={classes.imageText2}>
              <strong>
                Maximum intensity projection (MIP) of physiologic uptake. Standardized uptake value (SUV) scale is 0-10
              </strong>
            </Typography>
          </div>
          <Typography className={classes.abbreviations}>Abbreviations:</Typography>
          <Typography className={classes.abbreviationContent}>
            <sup className={classes.superscript2}>225</sup>
            Ac, actinium-225;
            <sup className={classes.superscript2}>177</sup>
            Lu, lutetium-177; PK, pharmacokinetics
          </Typography>
        </div>
      </div>
      <LearningCheckQuestions module="poslumaAcquisition" page={3} setNextDisabled={setNextDisabled} trainingId={2} />
    </>
  );
};

export default AcquisitionPage03;
